/**
 * Status Store - Global state management for real-time status data
 * Uses Zustand for reactive state updates
 */

import { create } from 'zustand';
import type {
  StatusData,
  CctvStatus,
  ParkingLocationStatus,
  LightingStatus,
  StatusMessage,
} from '../types/status';
import { statusService } from '../api/statusService';

interface StatusStore extends StatusData {
  // Connection state
  connected: boolean;
  /**
   * 유휴(절전) 베이스라인 표시 토글. true 면 맵의 모든 통로등 CCTV 를 기본 디밍(20%)
   * 으로 은은하게 그리고, 이벤트가 오면 그 위에 밝기를 덮어쓴다. 시뮬레이터 dry-run
   * 동안에만 켜고, 에디터/뷰어에서는 항상 false 라 영향이 없다.
   */
  lightingBaseline: boolean;
  /**
   * 비상 호출 중인 CCTV 이름 목록. 여기 포함된 CCTV 의 조명 bloom 은 lightType 과
   * 무관하게 점멸한다(비상벨 호출 → 디밍 깜빡임). 'alert' 시 추가, 'release' 시 제거.
   */
  emergencyCctvs: string[];
  /**
   * 비상 호출 CCTV 별 연결 조명 셀 id 목록. 맵에서 해당 조명 도형이 점멸한다.
   * setEmergencyLights('alert') 시 등록, 'release' 시 제거.
   */
  emergencyLightIdsByCctv: Record<string, string[]>;

  // Actions
  connect: () => void;
  disconnect: () => void;
  updateCctvStatus: (status: CctvStatus) => void;
  updateParkingStatus: (status: ParkingLocationStatus) => void;
  updateLightingStatus: (status: LightingStatus) => void;
  setLightingBaseline: (on: boolean) => void;
  setEmergencyCctv: (cctvName: string, on: boolean) => void;
  setEmergencyLights: (cctvName: string, lightIds: string[], on: boolean) => void;
  clearLighting: () => void;
  bulkUpdate: (data: StatusData) => void;
  clearStatuses: () => void;

  // Selectors (computed)
  getCctvStatus: (objectId: string) => CctvStatus | undefined;
  getParkingStatus: (objectId: string) => ParkingLocationStatus | undefined;
  getLightingStatus: (cctvName: string) => LightingStatus | undefined;
}

/**
 * Status store instance
 */
export const useStatusStore = create<StatusStore>((set, get) => ({
  // Initial state
  cctvStatuses: {},
  parkingStatuses: {},
  lightingStatuses: {},
  connected: false,
  lightingBaseline: false,
  emergencyCctvs: [],
  emergencyLightIdsByCctv: {},

  /**
   * Connect to status service and subscribe to updates
   */
  connect: () => {
    if (get().connected) return;

    // Subscribe to status updates
    const unsubscribe = statusService.subscribe((message: StatusMessage) => {
      switch (message.type) {
        case 'cctv_status':
          get().updateCctvStatus(message.data);
          break;
        case 'parking_status':
          get().updateParkingStatus(message.data);
          break;
        case 'lighting_status':
          get().updateLightingStatus(message.data);
          break;
        case 'bulk_update':
          get().bulkUpdate(message.data);
          break;
      }
    });

    // Connect to service
    statusService.connect();

    set({ connected: true });

    // Store unsubscribe function for cleanup
    (window as unknown as { __statusUnsubscribe?: () => void }).__statusUnsubscribe = unsubscribe;
  },

  /**
   * Disconnect from status service and clear all status data
   */
  disconnect: () => {
    if (!get().connected) return;

    statusService.disconnect();

    // Cleanup subscription
    const unsubscribe = (window as unknown as { __statusUnsubscribe?: () => void }).__statusUnsubscribe;
    if (unsubscribe) {
      unsubscribe();
      delete (window as unknown as { __statusUnsubscribe?: () => void }).__statusUnsubscribe;
    }

    // Clear all status data and set disconnected
    set({
      connected: false,
      cctvStatuses: {},
      parkingStatuses: {},
      lightingStatuses: {},
      lightingBaseline: false,
      emergencyCctvs: [],
      emergencyLightIdsByCctv: {},
    });
  },

  /**
   * Clear all status data without disconnecting
   */
  clearStatuses: () => {
    set({
      cctvStatuses: {},
      parkingStatuses: {},
      lightingStatuses: {},
      lightingBaseline: false,
      emergencyCctvs: [],
      emergencyLightIdsByCctv: {},
    });
  },

  /**
   * Update single CCTV status
   */
  updateCctvStatus: (status: CctvStatus) => {
    set(state => ({
      cctvStatuses: {
        ...state.cctvStatuses,
        [status.objectId]: status,
      },
    }));
  },

  /**
   * Update single parking status
   */
  updateParkingStatus: (status: ParkingLocationStatus) => {
    set(state => ({
      parkingStatuses: {
        ...state.parkingStatuses,
        [status.objectId]: status,
      },
    }));
  },

  /**
   * Update single lighting status (keyed by cctvName)
   */
  updateLightingStatus: (status: LightingStatus) => {
    set(state => ({
      lightingStatuses: {
        ...state.lightingStatuses,
        [status.cctvName]: status,
      },
    }));
  },

  /**
   * Toggle the idle(20%) baseline layer (simulator dry-run only)
   */
  setLightingBaseline: (on: boolean) => {
    set({ lightingBaseline: on });
  },

  /**
   * Mark/unmark a CCTV as being under an active emergency call. Its bloom blinks
   * while present. 'alert' → on=true (add), 'release' → on=false (remove).
   */
  setEmergencyCctv: (cctvName: string, on: boolean) => {
    if (!cctvName) return;
    set(state => {
      const has = state.emergencyCctvs.includes(cctvName);
      if (on === has) return state;
      return {
        emergencyCctvs: on
          ? [...state.emergencyCctvs, cctvName]
          : state.emergencyCctvs.filter(n => n !== cctvName),
      };
    });
  },

  /**
   * 비상 호출 CCTV 의 연결 조명 셀 id 를 등록/해제한다. 등록된 조명은 맵에서
   * 도형이 빨갛게 점멸한다(CanvasRenderer.renderEmergencyLights).
   */
  setEmergencyLights: (cctvName: string, lightIds: string[], on: boolean) => {
    if (!cctvName) return;
    set(state => {
      const next = { ...state.emergencyLightIdsByCctv };
      if (on && lightIds.length > 0) next[cctvName] = lightIds;
      else delete next[cctvName];
      return { emergencyLightIdsByCctv: next };
    });
  },

  /**
   * Clear only lighting statuses (e.g. when dry-run turns off)
   */
  clearLighting: () => {
    set({ lightingStatuses: {}, emergencyCctvs: [], emergencyLightIdsByCctv: {} });
  },

  /**
   * Bulk update all statuses
   */
  bulkUpdate: (data: StatusData) => {
    set({
      cctvStatuses: data.cctvStatuses,
      parkingStatuses: data.parkingStatuses,
      lightingStatuses: data.lightingStatuses ?? {},
    });
  },

  /**
   * Get CCTV status by object ID
   */
  getCctvStatus: (objectId: string) => {
    return get().cctvStatuses[objectId];
  },

  /**
   * Get parking status by object ID
   */
  getParkingStatus: (objectId: string) => {
    return get().parkingStatuses[objectId];
  },

  /**
   * Get lighting status by cctvName
   */
  getLightingStatus: (cctvName: string) => {
    return get().lightingStatuses[cctvName];
  },
}));

/**
 * Hook to get CCTV status for a specific object
 */
export function useCctvStatus(objectId: string): CctvStatus | undefined {
  return useStatusStore(state => state.cctvStatuses[objectId]);
}

/**
 * Hook to get parking status for a specific object
 */
export function useParkingStatus(objectId: string): ParkingLocationStatus | undefined {
  return useStatusStore(state => state.parkingStatuses[objectId]);
}

/**
 * Hook to get connection status
 */
export function useStatusConnection() {
  return useStatusStore(state => state.connected);
}

/**
 * Hook to get all lighting statuses (keyed by cctvName)
 */
export function useLightingStatuses(): Record<string, LightingStatus> {
  return useStatusStore(state => state.lightingStatuses);
}

/**
 * Hook to get lighting status for a specific CCTV
 */
export function useLightingStatus(cctvName: string): LightingStatus | undefined {
  return useStatusStore(state => state.lightingStatuses[cctvName]);
}
